import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAudit, useAuditFiltre } from "@/api/hooks/useAudit";
import { AuditSkupiny, TABULKY_SK } from "@/components/historia/AuditSkupiny";
import type { AuditFilters } from "@/api/queryKeys";

const PER_PAGE = 50;

/**
 * História zmien — kto, kedy a čo zmenil.
 *
 * KOLO 60 sem dalo taby Zmeny / Odpracovaný čas / Používatelia, KOLO 69 ich
 * dva odtiaľto **odobralo do Správy**: história je pracovná informácia pre
 * všetkých, kým čas a účty patria adminovi. Zostala jedna vec na jednej
 * stránke.
 *
 * **Filtre žijú v URL**, nie v `localStorage` — konkrétny výber sa dá poslať
 * kolegovi odkazom a z editora zákazky sem vedie `?zakazka_id=`.
 *
 * Prihlásenia (`LOGIN` / `LOGIN_FAILED`) v zozname vidí **len admin** —
 * filtruje ich backend, tu sa nič neskrýva.
 */
export function HistoriaPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const filters: AuditFilters = useMemo(
    () => ({
      zakazka_id: params.get("zakazka_id") ?? undefined,
      autor: params.get("autor") ?? undefined,
      tabulka: params.get("tabulka") ?? undefined,
      action: params.get("action") ?? undefined,
      od: params.get("od") ?? undefined,
      do: params.get("do") ?? undefined,
      page,
      per_page: PER_PAGE,
    }),
    [params, page],
  );

  const query = useAudit(filters);
  const filtreQuery = useAuditFiltre();
  const zaznamy = query.data?.data ?? [];
  const pagination = query.data?.pagination;
  const dostupne = filtreQuery.data?.data;

  function nastav(kluc: string, hodnota: string): void {
    const next = new URLSearchParams(params);
    if (hodnota) next.set(kluc, hodnota);
    else next.delete(kluc);
    setParams(next, { replace: true });
    // Nová filtrácia začína od prvej strany — inak by user videl
    // prázdno, lebo strana 4 v novom výbere neexistuje.
    setPage(1);
  }

  const maFilter = [
    "zakazka_id",
    "autor",
    "tabulka",
    "action",
    "od",
    "do",
  ].some((k) => params.get(k));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-3xl">
            <History className="h-7 w-7 text-primary" />
            História
          </h1>
          <p className="text-sm text-muted-foreground">
            Kto, kedy a čo zmenil.
          </p>
        </div>
        {maFilter ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setParams(new URLSearchParams(), { replace: true });
              setPage(1);
            }}
            title="Zrušiť všetky filtre"
          >
            Zrušiť filtre
          </Button>
        ) : null}
      </div>

      <section className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 md:grid-cols-3 lg:grid-cols-5">
        <div className="space-y-1">
          <Label htmlFor="h-autor" className="text-xs">
            Kto
          </Label>
          <select
            id="h-autor"
            value={params.get("autor") ?? ""}
            onChange={(e) => nastav("autor", e.target.value)}
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          >
            <option value="">všetci</option>
            {(dostupne?.autori ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="h-tabulka" className="text-xs">
            Čoho sa zmena týka
          </Label>
          <select
            id="h-tabulka"
            value={params.get("tabulka") ?? ""}
            onChange={(e) => nastav("tabulka", e.target.value)}
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          >
            <option value="">všetko</option>
            {(dostupne?.tabulky ?? []).map((t) => (
              <option key={t} value={t}>
                {TABULKY_SK[t] ?? t}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="h-akcia" className="text-xs">
            Druh zmeny
          </Label>
          <select
            id="h-akcia"
            value={params.get("action") ?? ""}
            onChange={(e) => nastav("action", e.target.value)}
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          >
            <option value="">všetko</option>
            <option value="INSERT">Vytvorené</option>
            <option value="UPDATE">Zmenené</option>
            <option value="DELETE">Zmazané</option>
            <option value="LOGIN">Prihlásenie</option>
            <option value="LOGIN_FAILED">Neúspešné prihlásenie</option>
          </select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="h-od" className="text-xs">
            Od
          </Label>
          <Input
            id="h-od"
            type="date"
            value={params.get("od") ?? ""}
            onChange={(e) => nastav("od", e.target.value)}
            className="h-9"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="h-do" className="text-xs">
            Do
          </Label>
          <Input
            id="h-do"
            type="date"
            value={params.get("do") ?? ""}
            onChange={(e) => nastav("do", e.target.value)}
            className="h-9"
          />
        </div>
      </section>

      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : zaznamy.length === 0 ? (
        <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          {maFilter
            ? "Pre zvolené filtre nie sú žiadne zmeny."
            : "História je prázdna."}
        </p>
      ) : (
        <AuditSkupiny
          zaznamy={zaznamy}
          filtrovanaZakazka={params.get("zakazka_id") ?? undefined}
          onFiltrujZakazku={(id) => nastav("zakazka_id", id)}
        />
      )}

      {pagination && pagination.total_pages > 1 ? (
        <div className="space-y-1">
          <div className="flex items-center justify-center gap-3 text-sm">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Predošlé
            </Button>
            <span className="text-muted-foreground">
              Strana {pagination.page} z {pagination.total_pages} ·{" "}
              {pagination.total} zmien
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Ďalšie
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            Strana je časový výsek {PER_PAGE} zmien, takže zákazka môže
            pokračovať na ďalšej strane — „len táto zákazka" v hlavičke skupiny
            ju zobrazí celú.
          </p>
        </div>
      ) : null}
    </div>
  );
}
